import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import Modal from './Modal';
import Confetti from './Confetti';
import Button from './Button';
import Icon from './Icon';
import type { FirstDepositBonusStatus } from '../hooks/useFirstDepositBonus';

const SEEN_KEY = 'fdb-welcome-seen';

/**
 * One-time celebration shown the moment a player's first-deposit bonus lands.
 * Checks the bonus status on mount, whenever an order updates (socket), and when
 * the tab regains focus — so it fires right after the deposit completes (the
 * card webhook flips `granted` true) — then shows once, guarded by localStorage.
 *
 * Distinct from FirstDepositBonusPopup, which is the *pre*-deposit nudge driven
 * by `eligible`; this one fires *after* the bonus is granted.
 */
export default function FirstDepositBonusGrantedPopup() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const [amount, setAmount] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const check = useCallback(async () => {
    if (localStorage.getItem(SEEN_KEY)) return;
    try {
      const res = await api.get<FirstDepositBonusStatus>('/promotions/first-deposit-bonus');
      if (res.data?.granted) {
        localStorage.setItem(SEEN_KEY, '1');
        setAmount(res.data.amount);
        setOpen(true);
      }
    } catch { /* best-effort — no popup on failure */ }
  }, []);

  // Re-check on mount + on every order update (a completing deposit grants it).
  useEffect(() => { check(); }, [check, orderUpdateCount]);

  // And when the player returns to the tab (e.g. back from the card checkout).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [check]);

  if (!open || amount == null) return null;

  return (
    <>
      <Confetti />
      <Modal open={open} onClose={() => setOpen(false)}>
        <div style={{ textAlign: 'center', padding: '8px 4px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, margin: '0 auto 16px',
            backgroundColor: colors.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="gift" size={28} color="#000000" />
          </div>
          <div style={{ fontSize: 20, fontWeight: 900, color: colors.text, marginBottom: 8 }}>
            {t('firstDepositBonus.grantedTitle')}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: colors.textSub, marginBottom: 20 }}>
            {t('firstDepositBonus.grantedBody', { amount: amount.toFixed(2) })}
          </div>
          <Button label={t('firstDepositBonus.grantedConfirm')} onClick={() => setOpen(false)} />
        </div>
      </Modal>
    </>
  );
}
